import { useCallback, useEffect, useState } from 'react';
import { useAsync } from 'react-async';
import cn from 'classnames';
import { useParams } from 'react-router-dom';

import { toast } from 'react-toastify';

import { bulkDeliverTickets, deliverTicket, getApprovedTicketList, getQrCodeStats, TicketRequestStatus } from '@core';
import Card from 'components/Card';
import Form from 'components/Form';
import styles from './TicketRequestList.module.sass';
import Table from './Table';
import { IEventListItem, IEventTicketListItem, IQrCodeStats } from '@types';
import QrCodes from '../QrCodes';
import { getApiErrorMessage } from '../errors';

const DEFAULT_ERROR = 'დაფიქსირდა შეცდომა, თავიდან სცადეთ';

export const TicketRequestList = ({ event }: { event: IEventListItem }) => {
  const params = useParams();
  const hasQrCodeLink = event.hasQrCodeLink ?? false;

  const [qrCodeStats, setQrCodeStats] = useState<IQrCodeStats>();

  const reloadQrCodeStats = useCallback(() => {
    if (!hasQrCodeLink || !params.id) {
      return;
    }
    getQrCodeStats(params.id)
      .then(response => setQrCodeStats(response.data))
      .catch(() => toast.error('QR კოდების რაოდენობის წამოღება ვერ მოხერხდა'));
  }, [hasQrCodeLink, params.id]);

  useEffect(() => {
    reloadQrCodeStats();
  }, [reloadQrCodeStats]);

  const [page, setPage] = useState(1);
  const [selectedTickets, setSelectedTickets] = useState<string[]>([]);

  const {
    data: usersList,
    isLoading: isUserListLoading,
    reload,
    run,
  } = useAsync({
    deferFn: getApprovedTicketList,
  });

  const [search, setSearch] = useState('');

  const handleApproveClickClick = async (item: IEventTicketListItem) => {
    try {
      const result = await deliverTicket(item.id);
      if (result.data) {
        toast.info('წარმატებულით მიენიჭა ბილეთი');
        reload();
        reloadQrCodeStats();
        return;
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, DEFAULT_ERROR));
      reloadQrCodeStats();
    }
  };

  const handleBulkApprove = useCallback(
    async (ticketIds: string[]) => {
      const confirmed = window.confirm(`ნამდვილად გსურთ ბილეთები გაცემა?`);
      if (!confirmed) {
        return;
      }

      try {
        const filteredTicketIds = usersList?.data.items.filter(
          ticket => ticket.status == TicketRequestStatus.Approved && ticketIds.includes(ticket.id),
        );
        if (!params.id || !filteredTicketIds || filteredTicketIds.length === 0) {
          return;
        }
        // One request, all or nothing: if anything fails (e.g. not enough QR codes) nothing is delivered.
        const result = await bulkDeliverTickets(
          params.id,
          filteredTicketIds.map(ticket => ticket.id),
        );
        toast.success(`${result.data} ბილეთი წარმატებით გაიცა`);
        reload();
        reloadQrCodeStats();
        setSelectedTickets([]); // Clear selection after bulk action
      } catch (err) {
        toast.error(getApiErrorMessage(err, DEFAULT_ERROR));
        reloadQrCodeStats();
      }
    },
    [reload, reloadQrCodeStats, params.id, usersList?.data?.items],
  );

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    const allTicketIds = usersList?.data?.items.map(ticket => ticket.id) || [];
    if (checked) {
      setSelectedTickets(allTicketIds);
    } else {
      setSelectedTickets([]);
    }
  };

  const handleSelectTicket = (ticketId: string, checked: boolean) => {
    if (checked) {
      setSelectedTickets(prev => [...prev, ticketId]);
    } else {
      setSelectedTickets(prev => prev.filter(id => id !== ticketId));
    }
  };

  // Clear selection when page changes
  useEffect(() => {
    setSelectedTickets([]);
  }, [page]);

  const handleSearchChange = (value: string) => {
    setPage(1);
    setSearch(value);
  };

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      run(params.id, page, search);
    }, 400); // 400ms debounce delay

    // Cleanup timeout if dependencies change before the delay finishes
    return () => clearTimeout(debounceTimeout);
  }, [page, search, run, params.id]);

  return (
    <>
      <Card className={styles.card} classTitle={cn('title-purple', styles.title)}>
        <br />
        {hasQrCodeLink && params.id && <QrCodes eventId={params.id} stats={qrCodeStats} onUploaded={setQrCodeStats} />}
        <div className={styles.search}>
          <Form
            className={styles.form}
            value={search}
            setValue={handleSearchChange}
            onSubmit={() => {}}
            placeholder="ძებნა"
            type="text"
            name="search"
            icon="search"
          />
        </div>
        <div className={cn(styles.row, { [styles.flex]: true })}>
          <Table
            tickets={usersList}
            isRequestLoading={isUserListLoading}
            onApprove={item => void handleApproveClickClick(item)}
            onBulkApprove={(ids: string[]) => void handleBulkApprove(ids)}
            selectedTickets={selectedTickets}
            onSelectAll={handleSelectAll}
            onSelectTicket={handleSelectTicket}
            className={styles.table}
            pageCount={usersList?.data.meta.totalPages ?? 0}
            handlePageChange={(selectedItem: { selected: number }) => {
              setPage(selectedItem.selected + 1);
            }}
          />
        </div>
      </Card>
    </>
  );
};
